import { I18nPluralPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, minLength } from '@angular/forms/signals';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ListPagination } from '@workspace/data-types';
import { filter, fromEvent, tap, throttleTime } from 'rxjs';
import {
  ColumnChangedDetection,
  ColumnRecord,
  ColumnsCustomizationAuraComponent,
} from '../columns-customization/columns-customization.component';
import { AuraDataChipsFilterHeaderFilters } from '../data-chips-filter-header/data-chips-filter-header.component';
import { DataHeaderAppliedFilter, DataHeaderFilter } from '../data-filters/data-header-filter.interface';
import { AuraDataHeaderFilters, FilterChangedDetection } from '../data-filters/data-header-filters.component';
import { TableColumnKey } from '../data-table.component';
import { ActionButton, ActionMenu, DataListColumn } from '../data-table.interface';

@Component({
  selector: 'aura-data-header-actions',
  templateUrl: './data-header-actions.component.html',
  imports: [
    MatFormField,
    MatInput,
    MatButton,
    MatMenuModule,
    MatIcon,
    FormField,
    NgClass,
    MatTooltip,
    I18nPluralPipe,
    MatFormFieldModule,
    MatDivider,
    ColumnsCustomizationAuraComponent,
    AuraDataHeaderFilters,
    AuraDataChipsFilterHeaderFilters,
  ],
  host: {
    '(window:keydown)': 'handleKeyboard($event)',
  },
})
export class AuraDataHeaderActions<K extends string> implements OnInit, AfterViewInit {
  @ViewChild('searchInputRef') searchInputRef!: ElementRef;

  hideSearch = input<boolean>(false);
  hideRefresh = input<boolean>(false);
  topMenu = input<ActionMenu | null>(null);
  topButtons = input<ActionButton[]>([]);
  selectedCount = input.required<number>();
  columns = model<DataListColumn<TableColumnKey | K>[]>([]);
  columnChanged = output<ColumnRecord>();
  filterChanged = output<FilterChangedDetection>();
  clearSelection = output<null>();
  filters = input<DataHeaderFilter<TableColumnKey | K>[]>([]);
  triggerFiltering = output<null>();
  triggerRefresh = output<null>();
  clearFiltering = output<null>();
  searchPlaceHolder = input<string>('Rechercher');
  pagination = input.required<ListPagination>();
  searchChanged = output<string>();
  searchChip = signal<string | null>(null);
  chips = signal<string[]>([]);
  disableActions = signal<boolean>(false);
  mappedFilters = input<DataHeaderAppliedFilter<TableColumnKey | K>[]>();
  removeFilter = output<{ value: string; key: TableColumnKey | K }>();
  filterTemplate = input<TemplateRef<Record<string, unknown>> | null>(null);
  Array = Array;
  loading = input<boolean>();

  searchModel = signal<string>('');
  searchFormTree = form(this.searchModel, (b) => {
    minLength(b, 3);
  });

  selectionMapping: Record<string, string> = {
    '=0': '',
    '=1': '1 sélectionné',
    other: '# sélectionnés',
  };
  destroyRef = inject(DestroyRef);

  filterView = output<string>();

  ngOnInit(): void {
    if (this.pagination().search) {
      this.searchModel.set(this.pagination().search ?? '');
      this.searchChip.set(this.pagination().search ?? null);
    }
  }

  @ViewChild('columnTrigger') columnTrigger!: MatMenuTrigger;

  handleKeyboard(event: KeyboardEvent) {
    if (event.altKey && event.code === 'KeyR') {
      event.preventDefault();

      if (!this.hideSearch() && !this.disableActions()) {
        this.doTriggerRefresh();
      }
    } else if (event.altKey && event.code === 'KeyC') {
      event.preventDefault();

      if (this.columnTrigger?.menuOpen) {
        this.columnTrigger.closeMenu();
      } else {
        this.columnTrigger?.openMenu();
      }
    } else if (event.altKey && event.code === 'KeyS') {
      event.preventDefault();
      if (!this.hideSearch() && !this.loading()) {
        this.triggerSearch(this.searchModel());
      }
    }
  }

  doTriggerRefresh() {
    this.doDisableActionTimeout();
    this.triggerRefresh.emit(null);
  }

  doDisableActionTimeout(timeout = 2000) {
    this.disableActions.set(true);
    setTimeout(() => {
      this.disableActions.set(false);
    }, timeout);
  }

  doColumnChanged(event: ColumnChangedDetection) {
    if (event.column) {
      this.columnChanged.emit(event.column);
    }
  }

  ngAfterViewInit(): void {
    if (!this.hideSearch()) {
      fromEvent<KeyboardEvent>(this.searchInputRef.nativeElement, 'keydown')
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter((event) => event.key === 'Enter'),
          throttleTime(500),
          filter(() => this.searchFormTree().valid()),
          tap(() => this.triggerSearch(this.searchModel())),
        )
        .subscribe();
    }
  }

  triggerSearch(searchValue: string | null): void {
    this.doDisableActionTimeout();
    if (searchValue === null || searchValue === '') {
      this.searchModel.set('');
      this.searchChip.set(null);
      this.searchChanged.emit('');
      return;
    }
    if (this.searchFormTree().valid()) {
      this.searchChip.set(searchValue);
      this.searchChanged.emit(searchValue);
    }
  }

  getButtonClass(button: ActionButton): string {
    switch (button.style) {
      case 'fab':
        return 'mat-fab';
      case 'mini-fab':
        return 'mat-mini-fab';
      case 'stroked':
        return 'mat-stroked-button';
      case 'flat':
        return 'mat-flat-button';
      case 'raised':
        return 'mat-raised-button';
      default:
        return 'mat-button';
    }
  }
}
